import React from 'react';
import { TFunction } from 'i18next';
import * as Yup from 'yup';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import _get from 'lodash/get';
import Typography from '@lib/components/Typography/Typography';
import { Steps } from '@lib/enums/form';
import Select, {
  SelectComponents,
  SelectProps,
} from '@lib/components/Select/Select';
import {
  getOptionShape,
  getOptionValidation,
} from '@lib/utils/validationUtils';
import styles from '@lib/assets/styles/Form.module.scss';
import { GetLoadOptions } from '@lib/components/Select/types';
import UserOption from '@lib/components/Select/components/UserOption';
import AreaOption from '@lib/components/Select/components/AreaOption';
import AssetOption from '@lib/components/Select/components/AssetOption';
import UserSingleValue from '@lib/components/Select/components/UserSingleValue';
import { AssignationsType, FieldNames } from 'routes/Task/enums';
import generateIdsFieldKey from '@lib/utils/generateIdsFieldKey';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import Trans from '@lib/components/Trans/Trans';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import TasksDuplicationWarning from 'routes/Task/TaskDuplicationWarning/TaskDuplicationWarning';

type OnViewFormTemplateType = (
  templateFormId: string,
  templateFormVersionId: string,
) => void;

type TaskFieldsArgs = {
  canQrCodeRelatedInfoUpdate?: boolean;
  getApproversLoadOptions: GetLoadOptions;
  getAssetsLoadOptions: GetLoadOptions;
  getSiteAreasLoadOptions: GetLoadOptions;
  getSitesLoadOptions: GetLoadOptions;
  getTemplatesLoadOptions?: GetLoadOptions;
  getTemplateVersionsLoadOptions?: GetLoadOptions;
  isCreate: boolean;
  isCreateForm?: boolean;
  onCreateNewFormTemplate?: () => void;
  onViewFormTemplate?: OnViewFormTemplateType;
  openAreasModalTableMultiselect?: () => void;
  openAssetsModalTableMultiselect?: () => void;
  openAreasModalTableMultiunselect?: () => void;
  openAssetsModalTableMultiunselect?: () => void;
  step?: Steps;
  t: TFunction<'translation', undefined>;
};

export default function getCreateTaskScopeFields(
  args: TaskFieldsArgs,
): FieldItem[] {
  const {
    getApproversLoadOptions,
    getAssetsLoadOptions,
    getSiteAreasLoadOptions,
    openAreasModalTableMultiselect,
    openAreasModalTableMultiunselect,
    openAssetsModalTableMultiselect,
    openAssetsModalTableMultiunselect,
    step = Steps.step1,
    t,
  } = args;
  return [
    {
      name: FieldNames.assignationsTitle,
      customRender: () => (
        <Typography variant="h3" className={styles.blockStart}>
          {t('task-scope')}
        </Typography>
      ),
      step,
    },
    {
      name: FieldNames.assignationsType,
      element: RadioButtonGroup,
      validation: getOptionValidation({ required: true, t }),
      componentProps: {
        options: [
          {
            label: t('area-based'),
            value: AssignationsType.Area,
            leftIcon: 'location_on',
          },
          {
            label: t('equipment-based'),
            value: AssignationsType.Equipment,
            leftIcon: 'inventory',
          },
        ],
        RadioButtonComponent: RadioButtonAsButton,
        wrapView: RadioButtonWrapViews.row,
      },
      step,
    },
    {
      name: FieldNames.assignationsTypeDesc,
      customRender: (values: Values) => {
        const assignationsType = _get(values, [
          FieldNames.assignationsType,
          'value',
        ]);
        if (assignationsType === AssignationsType.Area) {
          return (
            <SupportiveMessage
              text={t('task-area-assignation-desc')}
              color={SupportiveMessageColor.Gray}
              type={SupportiveMessageType.Transparent}
              leftIcon="info"
            />
          );
        }
        if (assignationsType === AssignationsType.Equipment) {
          return (
            <SupportiveMessage
              text={t('task-equipment-assignation-desc')}
              color={SupportiveMessageColor.Gray}
              type={SupportiveMessageType.Transparent}
              leftIcon="info"
            />
          );
        }
        return <div />;
      },
      isUseWatch: true,
      step,
    },
    {
      name: FieldNames.siteArea,
      label: t('areas-label'),
      element: Select,
      validation: Yup.array().when(FieldNames.assignationsType, {
        is: (assignationType: { value: string }) =>
          assignationType?.value === AssignationsType.Area,
        then: (schema) =>
          schema
            .of(
              Yup.object()
                .shape(getOptionShape(t, true))
                .required(t('required-field-error')),
            )
            .min(1, t('required-field-error'))
            .required(t('required-field-error')),
        otherwise: (schema) => schema.nullable(),
      }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        isClearable: false,
        isSearchable: false,
        openMenuOnClick: false,
        openMenuOnFocus: false,
        menuIsOpen: false,
        defaultOptions: false,
        getLoadOptions: getSiteAreasLoadOptions,
        onSelectWrapperMouseDown: openAreasModalTableMultiselect,
        components: {
          Option: AreaOption,
          ValueContainer: MultiSelectValueContainer,
        },
      } as SelectProps,
      isOptionalFn: (values: Values) =>
        _get(values, [FieldNames.assignationsType, 'value']) !==
        AssignationsType.Area,
      isVisibleFn: (values: Values) =>
        _get(values, [FieldNames.assignationsType, 'value']) ===
        AssignationsType.Area,
      step,
      fieldChangeCallBack: ({ setValue, name }: FieldChangeCallBackArgs) => {
        // TODO: here should be condition with `type === 'change'`
        if (name === FieldNames.siteArea) {
          setValue(FieldNames.approver, null, {
            shouldValidate: false,
          });
          setValue(FieldNames.sitesForApprover, [], {
            shouldValidate: true,
          });
        }
      },
    },
    {
      name: FieldNames.asset,
      label: t('asset'),
      element: Select,
      validation: Yup.array().when(FieldNames.assignationsType, {
        is: (assignationType: { value: string }) =>
          assignationType?.value === AssignationsType.Equipment,
        then: (schema) =>
          schema
            .of(
              Yup.object()
                .shape(getOptionShape(t, true))
                .required(t('required-field-error')),
            )
            .min(1, t('required-field-error'))
            .required(t('required-field-error')),
        otherwise: (schema) => schema.nullable(),
      }),
      componentProps: {
        isAsync: true,
        isMulti: true,
        isClearable: false,
        isSearchable: false,
        openMenuOnClick: false,
        openMenuOnFocus: false,
        menuIsOpen: false,
        defaultOptions: false,
        getLoadOptions: getAssetsLoadOptions,
        onSelectWrapperMouseDown: openAssetsModalTableMultiselect,
        components: {
          Option: AssetOption,
          ValueContainer: MultiSelectValueContainer,
        } as unknown as SelectComponents,
      } as SelectProps,
      isOptionalFn: (values: Values) =>
        _get(values, [FieldNames.assignationsType, 'value']) !==
        AssignationsType.Equipment,
      isVisibleFn: (values: Values) =>
        _get(values, [FieldNames.assignationsType, 'value']) ===
        AssignationsType.Equipment,
      step,
      fieldChangeCallBack: ({ setValue, name }: FieldChangeCallBackArgs) => {
        // TODO: here should be condition with `type === 'change'`
        if (name === FieldNames.asset) {
          setValue(FieldNames.approver, null, {
            shouldValidate: false,
          });
          setValue(FieldNames.sitesForApprover, [], {
            shouldValidate: true,
          });
        }
      },
    },
    {
      name: FieldNames.tasksDuplicationWarning,
      customRender: (values: Values) => {
        const assignationsType = _get(values, [
          FieldNames.assignationsType,
          'value',
        ]);
        const rawTaskName = _get(values, FieldNames.taskName);
        const taskName =
          typeof rawTaskName === 'string' && rawTaskName.trim().length
            ? rawTaskName.trim()
            : undefined;
        const templateVersionValue = _get(values, [
          FieldNames.templateVersion,
          'value',
        ]);
        const templateVersionId = templateVersionValue
          ? String(templateVersionValue)
          : undefined;
        const siteAreaValue = _get(values, FieldNames.siteArea);
        const siteAreaIds = Array.isArray(siteAreaValue)
          ? siteAreaValue
              .map((item) => {
                const value = _get(item, 'value');
                return value ? String(value) : null;
              })
              .filter((id): id is string => Boolean(id))
          : [];
        const assetValue = _get(values, FieldNames.asset);
        const assetIds = Array.isArray(assetValue)
          ? assetValue
              .map((item) => {
                const value = _get(item, 'value');
                return value ? String(value) : null;
              })
              .filter((id): id is string => Boolean(id))
          : [];
        return (
          <TasksDuplicationWarning
            assignationsType={assignationsType}
            assetIds={assetIds}
            siteAreaIds={siteAreaIds}
            taskName={taskName}
            templateVersionId={templateVersionId}
          />
        );
      },
      isUseWatch: true,
      hideErrors: true,
      step,
    },
    {
      name: FieldNames.approver,
      label: t('approver'),
      element: Select,
      validation: getOptionValidation({ t, required: true }),
      isUseWatch: true,
      getComponentProps: (values: Values) => {
        let disabledTooltipProps;
        const assignationsType = _get(values, [
          FieldNames.assignationsType,
          'value',
        ]);
        if (assignationsType === AssignationsType.Area) {
          disabledTooltipProps = {
            body: t('choose-site-area-first'),
          };
        } else if (assignationsType === AssignationsType.Equipment) {
          disabledTooltipProps = {
            body: t('choose-asset-first'),
          };
        }
        const idDistabled = !_get(values, [
          FieldNames.sitesForApprover,
          'length',
        ]);
        return {
          isAsync: true,
          getLoadOptions: getApproversLoadOptions,
          defaultOptions: false,
          components: {
            Option: UserOption,
            SingleValue: UserSingleValue,
          } as unknown as SelectComponents,
          disabledTooltipProps,
          disabled: idDistabled,
        };
      },
      getFieldKeyFn: (values: Values) => {
        const rawSiteIds = _get(
          values,
          FieldNames.sitesForApprover,
          [],
        ) as Array<{ value?: string } | string>;
        return generateIdsFieldKey(rawSiteIds, {
          prefix: `${FieldNames.approver}-sites`,
          emptyKey: `${FieldNames.approver}-no-sites`,
        });
      },
      step,
    },
    {
      name: FieldNames.tasksDetails,
      customRender: (values: Values) => {
        let count = 0;
        let onClick;
        const assignationsType = _get(values, [
          FieldNames.assignationsType,
          'value',
        ]);
        if (assignationsType === AssignationsType.Area) {
          count = _get(values, [FieldNames.siteArea, 'length'], 0);
          onClick = openAreasModalTableMultiunselect;
        } else if (assignationsType === AssignationsType.Equipment) {
          count = _get(values, [FieldNames.asset, 'length'], 0);
          onClick = openAssetsModalTableMultiunselect;
        }
        return (
          <SupportiveMessage
            text={<Trans i18nKey="tasks-to-be-created" values={{ count }} />}
            color={SupportiveMessageColor.Blue}
            actions={
              count ? (
                <Button
                  buttonText={t('detailed-summary')}
                  buttonType={ButtonTypes.primaryText}
                  onClick={onClick}
                />
              ) : undefined
            }
          />
        );
      },
      step,
    },
  ];
}
